// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { cn } from '@/lib/utils'

interface RadioOption {
  value:       string
  label:       string
  description?: string
}

interface RadioGroupProps {
  name:       string
  options:    RadioOption[]
  value:      string
  onChange:   (value: string) => void
  label?:     string
  error?:     string
  orientation?: 'vertical' | 'horizontal'
}

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  label,
  error,
  orientation = 'vertical',
}: RadioGroupProps) {
  return (
    <fieldset className="flex flex-col gap-2">
      {label && (
        <legend className="text-sm font-medium text-ink-2 mb-1">{label}</legend>
      )}
      <div
        className={cn(
          'flex gap-3',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        )}
      >
        {options.map((opt) => {
          const isSelected = value === opt.value
          return (
            <label
              key={opt.value}
              className={cn(
                'flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer',
                'transition-all duration-150',
                isSelected
                  ? 'border-sage-600 bg-sage-50'
                  : 'border-sand-300 bg-white hover:border-sage-300 hover:bg-sage-50/50',
              )}
            >
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="radio"
                  name={name}
                  value={opt.value}
                  checked={isSelected}
                  onChange={() => onChange(opt.value)}
                  className="peer sr-only"
                />
                <div
                  className={cn(
                    'h-5 w-5 rounded-full border-2 transition-all duration-150',
                    isSelected
                      ? 'border-sage-600 bg-sage-600'
                      : 'border-sand-300 bg-white',
                  )}
                />
                {isSelected && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-white" />
                  </div>
                )}
              </div>
              <span className="text-sm text-ink-2">
                {opt.label}
                {opt.description && (
                  <span className="block mt-0.5 text-xs text-ink-4">{opt.description}</span>
                )}
              </span>
            </label>
          )
        })}
      </div>
      {error && (
        <p className="text-xs text-status-alertText" role="alert">{error}</p>
      )}
    </fieldset>
  )
}
